import { useCallback, useState } from 'react';

import { TriggerCategory, TriggerCategoryType } from '@/modules/HealthPlan/HealthPlan.types.ts';
import { AGGREGATE_TRIGGER_KEY, EMBEDDED_TRIGGER_KEY } from './useTierFields';
import { TierField } from './useTiers';

interface Params {
  getFieldValuesById: (id?: string) => TierField | undefined;
  handleChangeValues: (id?: string) => (values: Partial<TierField>) => void;
}

export type TriggerFieldKey = typeof AGGREGATE_TRIGGER_KEY | typeof EMBEDDED_TRIGGER_KEY;

const useAddTriggerState = ({ getFieldValuesById, handleChangeValues }: Params) => {
  const [addTriggerVisible, setAddTriggerVisible] = useState(false);
  const [addTriggerType, setAddTriggerType] = useState<TriggerCategoryType>(TriggerCategory.FAMILY);
  const [pendingTierId, setPendingTierId] = useState<string>('');

  const handleOpenAddTrigger = useCallback((tierId: string, field: TriggerFieldKey) => {
    setPendingTierId(tierId);
    setAddTriggerType(field === AGGREGATE_TRIGGER_KEY ? TriggerCategory.FAMILY : TriggerCategory.EMBEDDED);
    setAddTriggerVisible(true);
  }, []);

  const handleTriggerCreated = useCallback(
    (triggerId: string, triggerType: TriggerCategoryType) => {
      const tierValues = getFieldValuesById(pendingTierId);
      if (tierValues) {
        const targetField =
          triggerType === TriggerCategory.FAMILY ? AGGREGATE_TRIGGER_KEY : EMBEDDED_TRIGGER_KEY;
        handleChangeValues(pendingTierId)({
          ...tierValues,
          [targetField]: triggerId,
        });
      }
    },
    [pendingTierId, getFieldValuesById, handleChangeValues]
  );

  const handleCloseAddTrigger = useCallback(() => {
    setAddTriggerVisible(false);
  }, []);

  return {
    addTriggerVisible,
    addTriggerType,
    handleOpenAddTrigger,
    handleTriggerCreated,
    handleCloseAddTrigger,
  };
};

export default useAddTriggerState;
